import check from '@assets/check.svg';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { GenderEnum } from '@gql/graphql';
import type { CardItemBadge } from '@presentation/components';
import { CardItem } from '@presentation/components';
import { useTranslation } from '@presentation/utils';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import type { FC } from 'react';
import { useCallback, useMemo } from 'react';
import * as Styled from './styled';
import type { MtCardItemAnimalProps } from './types';

export * from './types';

export const MtCardItemAnimal: FC<MtCardItemAnimalProps> = (props) => {
  const {
    onClick,
    image,
    name,
    gender,
    birthday,
    archived = false,
    availableForBreeding,
    ...rest
  } = props;
  const { translate } = useTranslation();

  const genderIcon = useMemo((): IconProp | undefined => {
    if (gender === GenderEnum.F) return 'venus';
    if (gender === GenderEnum.M) return 'mars';
  }, [gender]);

  const getAgeFromBirthday = useCallback(
    (bd: Dayjs) => {
      const totalMonths = dayjs().diff(bd, 'months', true);
      const years = Math.floor(totalMonths / 12);
      const months = Math.floor(totalMonths % 12);
      if (years > 0) {
        const suffix = translate(`glossary.${years > 1 ? 'years' : 'year'}`);
        return `${years.toString()} ${suffix}`;
      }
      const suffix = translate(`glossary.${months > 1 ? 'months' : 'month'}`);
      return `${months.toString()} ${suffix}`;
    },
    [translate],
  );

  return (
    <CardItem
      image={image}
      onClick={onClick}
      imageOverlay={archived && <>{translate('glossary.archives')}</>}
      content={{
        title: (
          <>
            {genderIcon && <Styled.GenderIcon icon={genderIcon} />}
            {name}
          </>
        ),
        subtitle: <>{birthday && getAgeFromBirthday(birthday)}</>,
      }}
      badges={[
        ...(!availableForBreeding
          ? []
          : [
              {
                label: translate('glossary.available'),
                image: check,
                color: 'blue',
              } as CardItemBadge,
            ]),
      ]}
      {...rest}
    />
  );
};
